import React from 'react';
import { resolveEstimateDescriptionInline } from '../../utils/estimate-description';
import { AccountDescriptionSettings } from '../../utils/description-settings';

interface DraperyOrderDescriptionSummaryTabProps {
    salesDescriptionSettingsMapped: { id: string; isEnabled: boolean }[];
    draperyItemDetail?: {
        pleatTypesWithPricing: { id: any; name: string }[];
    }[];
    data: any;
    editMode: boolean;
    descriptionSettings?: AccountDescriptionSettings | null;
}

export const DraperyOrderDescriptionSummaryTab: React.FC<DraperyOrderDescriptionSummaryTabProps> = ({
    salesDescriptionSettingsMapped,
    draperyItemDetail,
    data,
    editMode,
    descriptionSettings,
}) => {
    const isSettingEnabled = (id: string) =>
        salesDescriptionSettingsMapped.some(item => item.id === id && item.isEnabled);
    const isHeadingTypeEnabled = isSettingEnabled("salesheadingtype(ripplefoldorpleated)");
    const isPleatTypeEnabled = isSettingEnabled("salespleattype(ifpleated)");
    const isFullLengthEnabled = isSettingEnabled("salesfulllength");
    const isCafeEnabled = isSettingEnabled("salescafecurtain");
    const isMasterCarrierEnabled = isSettingEnabled("salesmastercarrier(ifripplefold)");
    const isMountEnabled = isSettingEnabled("salesmount(ifripplefold)");
    const isFunctionalityEnabled = isSettingEnabled("salesfunctionality");
    const isPanelConfigurationEnabled = isSettingEnabled("salespanelconfiguration");
    const isPanelWidthsEnabled = isSettingEnabled("salespanelwidths");
    const isRodWidthEnabled = isSettingEnabled("salesrodwidth(iffunctional)");
    const isFullnessEnabled = isSettingEnabled("salesfullness(iffunctional,ifstationary-controlledbywidthsperpanel)");
    const isFinishedLengthEnabled = isSettingEnabled("salesfinishedlength(ifripplefold)");
    const isReturnEnabled = isSettingEnabled("salesreturn");
    const isControlTypeEnabled = isSettingEnabled("salescontroltype(ifripplefold)");
    const isControlLocationEnabled = isSettingEnabled("salescontrollocation(ifripplefold)");
    const isLiningNoEnabled = isSettingEnabled("saleslining:no");
    const isLiningYesEnabled = isSettingEnabled("saleslining:yes");
    const isShowLiningNameEnabled = isSettingEnabled("salesshowliningname");
    const isLiningPurchaserIfComEnabled = isSettingEnabled("salesliningpurchaserifcom");
    const isLiningYardsIfComEnabled = isSettingEnabled("salesliningyardsifcom");
    const isLiningYardsIfWorkroomEnabled = isSettingEnabled("salesliningyardsifworkroompurchasing");
    const isInterliningNoEnabled = isSettingEnabled("salesinterlining:no");
    const isInterliningYesEnabled = isSettingEnabled("salesinterlining:yes");
    const isShowInterliningNameEnabled = isSettingEnabled("salesshowinterliningname");
    const isInterliningPurchaserIfComEnabled = isSettingEnabled("salesinterliningpurchaserifcom");
    const isInterliningYardsIfComEnabled = isSettingEnabled("salesinterliningyardsifcom");
    const isInterliningYardsIfWorkroomEnabled = isSettingEnabled("salesinterliningyardsifworkroompurchasing");

    const formatPleatType = (pleatType: any) => {
        let formattedPleatType = pleatType.replace(/\d{4}$/, "");

        formattedPleatType = formattedPleatType.replace(/_/g, " ");

        formattedPleatType = formattedPleatType.replace(
            /\b\w/g,
            (char: string) => char.toUpperCase()
        );
        return formattedPleatType;
    };

    const getPleatTypeName = () => {
        const design = data.draperyOrderDesign;

        if (design && design.headingType && design.headingType === "ripplefold") {
            return "";
        }

        if (draperyItemDetail && draperyItemDetail.length > 0 && draperyItemDetail[0] && draperyItemDetail[0].pleatTypesWithPricing) {
            return formatPleatType(data.draperyOrderDesign.pleatType.toLowerCase());
        }

        return "";
    };

    const getMasterCarrierName = () => {
        const design = data.draperyOrderDesign;
        const ripplefoldOptions = design && design.ripplefoldOptions;
        const masterCarrier = ripplefoldOptions && ripplefoldOptions.masterCarrier;

        if (design && design.headingType === "ripplefold" && masterCarrier) {
            if (masterCarrier === "butt") {
                return "Butt";
            }

            if (masterCarrier === "overlap") {
                return "Overlap";
            }
        }

        return "";
    };

    const getMountName = () => {
        const design = data.draperyOrderDesign;
        const ripplefoldOptions = design && design.ripplefoldOptions;
        const mount = ripplefoldOptions && ripplefoldOptions.mount;

        if (design && design.headingType === "ripplefold" && mount) {
            return mount.charAt(0).toUpperCase() + mount.slice(1);
        }

        return "";
    };

    const getControlInfo = () => {
        const design = data.draperyOrderDesign;
        const ripplefoldOptions = design && design.ripplefoldOptions;

        if ((isControlTypeEnabled || isControlLocationEnabled) && design && design.headingType && design.headingType === "ripplefold") {
            let nameParts = [];

            const controlType = ripplefoldOptions && ripplefoldOptions.controlType
            const controlLocation = ripplefoldOptions && ripplefoldOptions.controlLocation

            if (controlType && isControlTypeEnabled) {
                const formatControlType = controlType.charAt(0).toUpperCase() + controlType.slice(1)
                nameParts.push(formatControlType)
            }

            if (controlLocation && isControlLocationEnabled) {
                const formatControlLocation = controlLocation.charAt(0).toUpperCase() + controlLocation.slice(1)
                nameParts.push(formatControlLocation)
            }

            return nameParts.join(" - ");
        }

        return;
    };

    const panelValues = Object.values(data.draperyOrderDesign.panelJson || {})
        .filter(value => value !== undefined);

    const panelTypeDisplayMap = {
        'center': 'Center Panel',
        'left': 'Left Panel',
        'right': 'Right Panel',
        'pair': 'Pair',
        'three-panels': '3 Panels',
        'four-panels': '4 Panels',
        'five-panels': '5 Panels'
    };

    type PanelKey = keyof typeof panelTypeDisplayMap;

    const panelType: PanelKey = data.draperyOrderDesign.panelType

    const displayPanelType = panelTypeDisplayMap[panelType] || '';

    const rodWidth = data && data.draperyOrderDesign && data.draperyOrderDesign.rodWidth
    const returnSize = data && data.draperyOrderDesign && data.draperyOrderDesign.customReturnOverlapJson && data.draperyOrderDesign.customReturnOverlapJson.returnSize

    const draperyFullnessValue = data && data.draperyOrderDesign && data.draperyOrderDesign.draperyFullness
    let draperyFullness
    if (data.draperyOrderDesign.headingType === "ripplefold") {
        draperyFullness = parseFloat(draperyFullnessValue) * 100
        draperyFullness = `${draperyFullness}%`
    } else {
        draperyFullness = `${draperyFullnessValue}x`
    }
    const shouldShowRodWidth = Boolean(isRodWidthEnabled && data.draperyOrderDesign.category === 0 && rodWidth);
    const shouldShowFullness = Boolean(isFullnessEnabled && data.draperyOrderDesign.category === 0 && draperyFullness);
    const shouldShowFinishedLength = Boolean(
        isFinishedLengthEnabled &&
        data.draperyOrderDesign.finishedLengthOfPanels &&
        data.draperyOrderDesign.finishedLengthOfPanels > 0
    );
    const shouldShowReturn = Boolean(isReturnEnabled && returnSize);
    
    const shouldShowReturnSeparator = shouldShowReturn && (shouldShowRodWidth || shouldShowFullness || shouldShowFinishedLength);

    const liningPatternItems = data.liningsPatternItems || [];
    const liningItem = data.liningsList.find((l: any) => l.name === "Lining" && l.active)
    const liningOrderOrigin = liningItem && liningItem.draperyOrderLiningsFabric && liningItem.draperyOrderLiningsFabric.orderOrigin
    const liningItemName = resolveEstimateDescriptionInline({
        fabric: liningItem && liningItem.draperyOrderLiningsFabric,
        items: liningPatternItems,
        fallbackMode: "pattern-only",
        descriptionSettings,
    })
    const liningYardsNeeded = liningItem && liningItem.draperyOrderLiningsFabric && liningItem.draperyOrderLiningsFabric.yardsNeeded
    const interliningItem = data.liningsList.find((l: any) => l.name === "Interlining" && l.active)
    const interliningOrderOrigin = interliningItem && interliningItem.draperyOrderLiningsFabric && interliningItem.draperyOrderLiningsFabric.orderOrigin
    const interliningItemName = resolveEstimateDescriptionInline({
        fabric: interliningItem && interliningItem.draperyOrderLiningsFabric,
        items: liningPatternItems,
        fallbackMode: "pattern-only",
        descriptionSettings,
    })
    const interliningYardsNeeded = interliningItem && interliningItem.draperyOrderLiningsFabric && interliningItem.draperyOrderLiningsFabric.yardsNeeded

    const hasLiningItem = Boolean(liningItem);
    const hasInterliningItem = Boolean(interliningItem);
    const shouldShowLiningSection =
        isLiningNoEnabled ||
        isLiningYesEnabled ||
        isShowLiningNameEnabled ||
        isLiningPurchaserIfComEnabled ||
        isLiningYardsIfComEnabled ||
        isLiningYardsIfWorkroomEnabled;
    const shouldShowInterliningSection =
        isInterliningNoEnabled ||
        isInterliningYesEnabled ||
        isShowInterliningNameEnabled ||
        isInterliningPurchaserIfComEnabled ||
        isInterliningYardsIfComEnabled ||
        isInterliningYardsIfWorkroomEnabled;

    const shouldShowLiningYes = hasLiningItem && isLiningYesEnabled;
    const shouldShowLiningName = hasLiningItem && isShowLiningNameEnabled && Boolean(liningItemName);
    const shouldShowLiningCom = hasLiningItem && liningOrderOrigin === 2 && isLiningPurchaserIfComEnabled;
    const shouldShowLiningYardsFromCom = hasLiningItem && liningOrderOrigin === 2 && isLiningYardsIfComEnabled && Boolean(liningYardsNeeded);
    const shouldShowLiningYardsFromWorkroom = hasLiningItem && liningOrderOrigin === 1 && isLiningYardsIfWorkroomEnabled && Boolean(liningYardsNeeded);
    const shouldShowLiningYards = shouldShowLiningYardsFromCom || shouldShowLiningYardsFromWorkroom;
    const liningDetailParts: string[] = [];
    if (hasLiningItem) {
        if (shouldShowLiningYes) {
            liningDetailParts.push("Yes");
        }
        if (shouldShowLiningName) {
            liningDetailParts.push(liningItemName);
        }
        if (shouldShowLiningYards) {
            liningDetailParts.push(`${liningYardsNeeded} yards`);
        }
    } else if (isLiningNoEnabled) {
        liningDetailParts.push("No");
    }
    const shouldRenderLining =
        shouldShowLiningSection &&
        ((hasLiningItem && (liningDetailParts.length > 0 || shouldShowLiningCom)) ||
            (!hasLiningItem && liningDetailParts.length > 0));

    const shouldShowInterliningYes = hasInterliningItem && isInterliningYesEnabled;
    const shouldShowInterliningName = hasInterliningItem && isShowInterliningNameEnabled && Boolean(interliningItemName);
    const shouldShowInterliningCom = hasInterliningItem && interliningOrderOrigin === 2 && isInterliningPurchaserIfComEnabled;
    const shouldShowInterliningYardsFromCom = hasInterliningItem && interliningOrderOrigin === 2 && isInterliningYardsIfComEnabled && Boolean(interliningYardsNeeded);
    const shouldShowInterliningYardsFromWorkroom = hasInterliningItem && interliningOrderOrigin === 1 && isInterliningYardsIfWorkroomEnabled && Boolean(interliningYardsNeeded);
    const shouldShowInterliningYards = shouldShowInterliningYardsFromCom || shouldShowInterliningYardsFromWorkroom;
    const interliningDetailParts: string[] = [];
    if (hasInterliningItem) {
        if (shouldShowInterliningYes) {
            interliningDetailParts.push("Yes");
        }
        if (shouldShowInterliningName) {
            interliningDetailParts.push(interliningItemName);
        }
        if (shouldShowInterliningYards) {
            interliningDetailParts.push(`${interliningYardsNeeded} yards`);
        }
    } else if (isInterliningNoEnabled) {
        interliningDetailParts.push("No");
    }
    const shouldRenderInterlining =
        shouldShowInterliningSection &&
        ((hasInterliningItem && (interliningDetailParts.length > 0 || shouldShowInterliningCom)) ||
            (!hasInterliningItem && interliningDetailParts.length > 0));

    const pleatTypeName = getPleatTypeName();
    const masterCarrierName = getMasterCarrierName();
    const mountName = getMountName();
    const controlTypeLocation = getControlInfo();
    const { headingType, draperyType } = data.draperyOrderDesign;
    let headingText = "";
    if (headingType === "rodandrings") {
        headingText = "Pleated";
    } else if (headingType === "ripplefold") {
        headingText = "Ripplefold";
    }

    const shouldShowHeading = isHeadingTypeEnabled && Boolean(headingText);
    const shouldShowPleatType = isPleatTypeEnabled && Boolean(pleatTypeName);
    const shouldShowFullLength = draperyType === "full-length" && isFullLengthEnabled;
    const shouldShowCafeCurtain = draperyType === "cafe-curtain" && isCafeEnabled;
    const shouldShowDraperyType =
        (shouldShowFullLength || shouldShowCafeCurtain) && headingType !== "ripplefold";
    const shouldShowMasterCarrier =
        headingType === "ripplefold" && isMasterCarrierEnabled && Boolean(masterCarrierName);
    const shouldShowMount =
        headingType === "ripplefold" && isMountEnabled && Boolean(mountName);
    const headerSegments: React.ReactNode[] = [];

    if (shouldShowHeading) {
        headerSegments.push(
            <span className="boldText">{headingText}</span>
        );
    }

    if (shouldShowPleatType) {
        headerSegments.push(
            <span className="boldText" style={{ textTransform: "capitalize" }}>{pleatTypeName}</span>
        );
    }

    if (shouldShowMasterCarrier) {
        headerSegments.push(
            <span className="boldText">{masterCarrierName}</span>
        );
    }

    if (shouldShowMount) {
        headerSegments.push(
            <span className="boldText">{mountName}</span>
        );
    }

    if (shouldShowDraperyType) {
        headerSegments.push(
            <span className="boldText">{shouldShowFullLength ? "Full Length" : "Cafe Curtain"}</span>
        );
    }
    const hasControlInfo = Boolean(controlTypeLocation && (isControlLocationEnabled || isControlTypeEnabled));
    const hasFunctionalityContent = Boolean(
        isFunctionalityEnabled &&
        (data.draperyOrderDesign.category === 0 || data.draperyOrderDesign.category === 1)
    );
    const hasPanelConfigurationContent = Boolean(isPanelConfigurationEnabled && displayPanelType);
    const hasFunctionalityOrPanelContent = hasFunctionalityContent || hasPanelConfigurationContent;
    const hasPanelWidthsContent = Boolean(isPanelWidthsEnabled && panelValues && panelValues.length > 0);
    const hasPrimaryContentExcludingHeader =
        hasControlInfo ||
        hasFunctionalityOrPanelContent ||
        hasPanelWidthsContent ||
        shouldShowRodWidth ||
        shouldShowFullness ||
        shouldShowFinishedLength ||
        shouldShowReturn;
    const shouldShowHeaderBreak = headerSegments.length > 0 && hasPrimaryContentExcludingHeader;
    const shouldShowLiningBreak =
        (shouldRenderLining || shouldRenderInterlining) &&
        (headerSegments.length > 0 || hasPrimaryContentExcludingHeader);

    return (
        <>
            <>
                {headerSegments.map((segment, index) => (
                    <React.Fragment key={`header-segment-${index}`}>
                        {index > 0 && <span className="boldText"> / </span>}
                        {segment}
                    </React.Fragment>
                ))}
            </>

            {shouldShowHeaderBreak && <br />}

            {hasControlInfo &&
                <>
                    <span><span className="boldText">Control:</span> {controlTypeLocation}</span>
                    <br />
                </>
            }
            <>
                {isFunctionalityEnabled && data.draperyOrderDesign.category === 0 && <span className="boldText">Functional</span>}
                {isFunctionalityEnabled && data.draperyOrderDesign.category === 1 && <span className="boldText">Stationary</span>}
                {isPanelConfigurationEnabled && displayPanelType && (
                    <span className="boldText">
                        {isFunctionalityEnabled && (data.draperyOrderDesign.category === 0 || data.draperyOrderDesign.category === 1) ? <span> / </span> : ''}
                        {displayPanelType}
                    </span>
                )}
            </>

            {hasPanelWidthsContent &&
                <>
                    {hasFunctionalityOrPanelContent && <br />}
                    <span className="boldText">Panel Widths:</span>
                    <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                        {panelValues.map(value => value + "w").join(" + ")}
                    </span>
                </>
            }

            {shouldShowRodWidth &&
                <>
                    <br />
                    <span className="boldText">Rod Width: </span>
                    <span>{rodWidth}"</span>
                </>
            }
            {shouldShowFullness &&
                <>
                    {!shouldShowRodWidth && hasPanelWidthsContent ? <br /> : ''}
                    {shouldShowRodWidth ? <span> / </span> : ''}
                    <span><span className="boldText">Fullness:</span> {draperyFullness}</span>
                </>
            }

            {shouldShowFinishedLength &&
                <>
                    <br className="boldText"></br>
                    <span className="nowrap">
                        <span className="boldText">Finished Length: </span>
                        <span className="nonBold">{data.draperyOrderDesign.finishedLengthOfPanels && data.draperyOrderDesign.finishedLengthOfPanels !== -1 ? data.draperyOrderDesign.finishedLengthOfPanels : '0'}"</span>
                    </span>
                </>
            }

            {shouldShowReturn &&
                <>
                    {shouldShowReturnSeparator ? <span> / </span> : <br className="boldText"></br>}
                    <span><span className="boldText">Return: </span>{returnSize}"</span>
                </>
            }

            {shouldShowLiningBreak && <br />}

            <>
                {shouldRenderLining && (
                    <>
                        {shouldShowLiningCom &&
                            <span className="boldText">
                                {`COM `}
                            </span>
                        }
                        <span className="boldText">
                            Lining:
                        </span>
                        {liningDetailParts.length > 0 && (
                            <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                                {liningDetailParts.join(", ")}
                            </span>
                        )}
                        <br />
                    </>
                )}
                {shouldRenderInterlining && (
                    <>
                        {shouldShowInterliningCom &&
                            <span className="boldText">
                                {`COM `}
                            </span>
                        }
                        <span className="boldText">
                            Interlining:
                        </span>
                        {interliningDetailParts.length > 0 && (
                            <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                                {interliningDetailParts.join(", ")}
                            </span>
                        )}
                        <br />
                    </>
                )}
            </>
        </>
    );
};
